/**
 * ChartTile — a bar or line chart of up to four series.
 *
 * data: { chart: "bar"|"line"; labels: string[];
 *         series: { name: string; values: (number|null)[] }[]; unit? }
 *
 * Plain SVG, no chart library: the shapes are bounded (100 points, 4
 * series) and a library would be the pane's largest dependency for two
 * forms. Colours are design tokens (fill-/stroke- classes), so both
 * themes follow. A `null` value is a gap, not a zero.
 */
import { cn } from "@/lib/cn";
import type { TileBodyProps } from "../registry";

interface Series {
  name?: string;
  values: (number | null)[];
}

interface ChartData {
  chart?: "bar" | "line";
  labels?: string[];
  series?: Series[];
  unit?: string;
}

// One token per series slot (components.MAX_SERIES = 4).
const FILL = ["fill-accent", "fill-success", "fill-warning", "fill-fg-muted"];
const STROKE = ["stroke-accent", "stroke-success", "stroke-warning", "stroke-fg-muted"];
const SWATCH = ["bg-accent", "bg-success", "bg-warning", "bg-fg-muted"];

const W = 300;
const H = 120;
const PAD_L = 30;
const PAD_B = 16;

function fmt(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(a >= 1e4 ? 0 : 1)}k`;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function ChartTile({ data }: TileBodyProps) {
  const d = data as ChartData;
  const labels = d.labels ?? [];
  const series = (d.series ?? []).slice(0, 4);
  const values = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  if (!labels.length || !values.length) {
    return <p className="text-[12.5px] text-fg-faint">No data to chart.</p>;
  }

  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const range = max - min || 1;
  const plotW = W - PAD_L;
  const plotH = H - PAD_B;
  const y = (v: number) => plotH - ((v - min) / range) * plotH;
  const slot = plotW / labels.length;
  const x = (i: number) => PAD_L + slot * i + slot / 2;
  // Label every nth tick so long series stay readable.
  const every = Math.ceil(labels.length / 8);
  const line = d.chart === "line";

  return (
    <div className="flex h-full flex-col gap-1.5">
      {(series.length > 1 || d.unit) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-fg-muted">
          {series.length > 1 &&
            series.map((s, i) => (
              <span key={i} className="flex items-center gap-1">
                <span className={cn("h-2 w-2", SWATCH[i])} />
                {s.name || `Series ${i + 1}`}
              </span>
            ))}
          {d.unit && <span className="ml-auto text-fg-faint">{d.unit}</span>}
        </div>
      )}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="min-h-0 w-full flex-1"
        role="img"
        aria-label={`${line ? "Line" : "Bar"} chart: ${series.map((s) => s.name).filter(Boolean).join(", ")}`}
      >
        {[max, (max + min) / 2, min].map((v, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={W} y1={y(v)} y2={y(v)} className="stroke-border" strokeWidth={0.5} />
            <text x={PAD_L - 3} y={y(v) + 3} textAnchor="end" className="fill-fg-faint" fontSize={8}>
              {fmt(v)}
            </text>
          </g>
        ))}
        {labels.map((label, i) =>
          i % every === 0 ? (
            <text key={i} x={x(i)} y={H - 4} textAnchor="middle" className="fill-fg-faint" fontSize={8}>
              {label.length > 8 ? `${label.slice(0, 7)}…` : label}
            </text>
          ) : null,
        )}
        {series.map((s, si) => {
          if (line) {
            // Break the path at gaps rather than drawing through them.
            let path = "";
            let pen = false;
            s.values.forEach((v, i) => {
              if (v === null) {
                pen = false;
                return;
              }
              path += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
              pen = true;
            });
            return (
              <path
                key={si}
                d={path}
                fill="none"
                className={STROKE[si]}
                strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
              />
            );
          }
          const barW = (slot * 0.8) / series.length;
          return s.values.map((v, i) =>
            v === null ? null : (
              <rect
                key={`${si}-${i}`}
                x={PAD_L + slot * i + slot * 0.1 + barW * si}
                y={Math.min(y(v), y(0))}
                width={Math.max(barW - 0.5, 0.5)}
                height={Math.abs(y(v) - y(0))}
                className={FILL[si]}
              >
                <title>{`${s.name ? `${s.name} · ` : ""}${labels[i] ?? ""}: ${v}${d.unit ? ` ${d.unit}` : ""}`}</title>
              </rect>
            ),
          );
        })}
      </svg>
    </div>
  );
}
